import { MutableRefObject, ReactNode, useRef, useState } from 'react';

import { DropdownProps } from '@sternum/dropdown/lib/Dropdown.component';
import { Dropdown } from '@sternum/ui';

import { AutocompleteDropdownTextInput } from './AutocompleteDropdown.styles';

type FilterOptionInChildren = <OptionType>(
  options: OptionType[],
  optionMapper: (filteredOption: OptionType, index: number) => ReactNode,
  filterOption: (searchValue: string, filterOption: OptionType) => boolean,
) => ReactNode[];

export interface AutocompleteDropdown extends Omit<DropdownProps, 'printValue' | 'children'> {
  printSelectedValue?: (value?: string) => string;
  printDropdownValue?: (args: {
    inputRef: MutableRefObject<HTMLInputElement | null>;
    value: string;
    valueToDisplay: string;
    searchValue?: string;
    isDropdownOpen: boolean;
    onChangeSearchValue: (updatedSearchValue: string) => unknown;
    // TODO: Maybe add input component
  }) => ReactNode;
  children?: (args: { searchValue?: string; filterOptions: FilterOptionInChildren }) => ReactNode;
}

export function AutocompleteDropdown({
  isOpen,
  children,
  printSelectedValue = (value) => value || '',
  printDropdownValue,
  ...props
}: AutocompleteDropdown) {
  const [isVisible, setIsVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const canClickAwayRef = useRef(true);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isDropdownOpen = isOpen ?? isVisible;

  const printValue = (value: string) => {
    const printedSelectedValue = printSelectedValue(value);

    if (printDropdownValue) {
      return printDropdownValue({
        inputRef,
        value,
        valueToDisplay: printedSelectedValue,
        searchValue,
        isDropdownOpen,
        onChangeSearchValue: setSearchValue,
      });
    }

    const inputValue = isDropdownOpen ? searchValue ?? printedSelectedValue : printedSelectedValue;

    return (
      <AutocompleteDropdown.TextInput
        ref={inputRef}
        type="text"
        variant="inline"
        width={props.fullWidth ? '100%' : undefined}
        placeholder={props.placeholder}
        value={inputValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />
    );
  };

  const handleClickDropdown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setIsVisible(true);

    canClickAwayRef.current = false;
    setTimeout(() => (canClickAwayRef.current = true), 0);

    props.onClick?.(e);
  };

  const handleClickAway = (e: MouseEvent) => {
    if (!canClickAwayRef.current) {
      return;
    }

    setIsVisible(false);
    setSearchValue(undefined);
    inputRef.current?.blur?.();

    props.popupProps?.onClickAway?.(e);
  };

  const handleChange = (value?: string) => {
    setIsVisible(false);
    setSearchValue(undefined);

    props.onChange?.(value);
  };

  const filterOptionsInChildren: FilterOptionInChildren = (options, optionMapper, filterOption): ReactNode[] => {
    return options.filter((option) => filterOption(searchValue || '', option)).map(optionMapper);
  };

  return (
    <Dropdown
      {...props}
      style={{ width: props.fullWidth ? '100%' : undefined, ...props.style }}
      isOpen={isDropdownOpen}
      printValue={printValue}
      onClick={handleClickDropdown}
      onChange={handleChange}
      popupProps={{
        ...props.popupProps,
        onClickAway: handleClickAway,
      }}
    >
      {children?.({ searchValue, filterOptions: filterOptionsInChildren })}
    </Dropdown>
  );
}

AutocompleteDropdown.TextInput = AutocompleteDropdownTextInput;
